import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Avatar, AvatarGroup, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { Forum } from '@mui/icons-material';
import PostCard from './PostCard';
import PostComposer from './PostComposer';
import CheckInDialog from './CheckInDialog';
import ConditionsReportDialog from './ConditionsReportDialog';
import SiteConditionsSummaryCard from './SiteConditionsSummaryCard';
import SignInNudge from './SignInNudge';
import { useNotification } from './NotificationContext';
import { authService } from '../services/authService';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { postService, Post, SiteConditionsSummary, photoUrl } from '../services/postService';
import { DiveSite } from '../types/DiveSite';
import { regionSlug } from '../utils/regions';
import { isVideoKey } from '../utils/media';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

/**
 * The social half of a dive site page: what divers have recently posted from here, who calls it
 * their home site, and a way to add a conditions report or photo. Everything shown is Public
 * content the authors chose to share — no dive history is aggregated across users.
 */
const SiteCommunity: React.FC<{ site: DiveSite }> = ({ site }) => {
  const { showSuccess } = useNotification();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const userId = currentUser?.userId ?? null;
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [divers, setDivers] = useState<UserPublicProfile[]>([]);
  const [composerOpen, setComposerOpen] = useState(false);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const [conditions, setConditions] = useState<SiteConditionsSummary | null>(null);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    postService.getSiteFeed(site.slug, site.latitude, site.longitude, site.matchRadiusKm || 5)
      .then(p => { if (!cancelled) setPosts(p); })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    postService.getSiteConditions(site.slug)
      .then(s => { if (!cancelled) setConditions(s); })
      .catch(() => { if (!cancelled) setConditions(null); });
    buddyService.discoverUsers()
      .then(all => {
        if (cancelled) return;
        const names = [site.name, ...(site.aliases ?? [])].map(n => n.trim().toLowerCase());
        setDivers(all.filter(d => (d.homeSiteId && d.homeSiteId === site.id) || (d.homeSiteName && names.includes(d.homeSiteName.trim().toLowerCase()))));
      })
      .catch(() => { if (!cancelled) setDivers([]); });
    return () => { cancelled = true; };
  }, [userId, site.id, site.slug, site.name, site.latitude, site.longitude, site.matchRadiusKm, site.aliases]);

  // Public check-ins from the last 24 hours — "who's diving here today".
  const hereToday = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const seen = new Set<string>();
    return posts.filter(p => {
      if (p.type !== 'CheckIn' || new Date(p.createdAt).getTime() < cutoff || seen.has(p.authorUserId)) return false;
      seen.add(p.authorUserId);
      return true;
    });
  }, [posts]);

  const recentPhotos = useMemo(
    () => posts.flatMap(p => p.photoKeys.filter(k => !isVideoKey(k)).map(k => ({ key: k, postId: p.postId }))).slice(0, 8),
    [posts]
  );

  if (!userId) {
    return (
      <Paper sx={{ p: 2.5, mt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Community at {site.name}</Typography>
        <SignInNudge
          feature={`see who's diving ${site.name}`}
          description="Recent photos, conditions reports, and local divers — sign in or create a free account to join the conversation."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2.5, mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Forum color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Community at {site.name}</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" size="small" onClick={() => setCheckInOpen(true)}>Check in</Button>
          <Button variant="outlined" size="small" onClick={() => setConditionsOpen(true)}>Report conditions</Button>
          <Button variant="contained" size="small" onClick={() => setComposerOpen(true)}>Share a photo</Button>
        </Stack>
      </Stack>

      {conditions && <SiteConditionsSummaryCard summary={conditions} />}

      {hereToday.length > 0 && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <AvatarGroup max={6}>
            {hereToday.map(p => (
              <Avatar
                key={p.postId}
                component={RouterLink}
                to={`/u/${p.authorUserId}`}
                alt={p.authorDisplayName}
                src={p.authorProfilePhotoUrl ?? undefined}
                sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'success.main', textDecoration: 'none' }}
              >
                {!p.authorProfilePhotoUrl && (initials(p.authorDisplayName) || '?')}
              </Avatar>
            ))}
          </AvatarGroup>
          <Typography variant="body2" color="text.secondary">
            {hereToday.length} diver{hereToday.length === 1 ? '' : 's'} checked in here today
          </Typography>
        </Stack>
      )}

      {divers.length > 0 && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <AvatarGroup max={6}>
            {divers.map(d => (
              <Avatar
                key={d.userId}
                component={RouterLink}
                to={`/u/${d.userId}`}
                alt={d.displayName}
                src={d.profilePhotoUrl ?? undefined}
                sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main', textDecoration: 'none' }}
              >
                {!d.profilePhotoUrl && (initials(d.displayName) || '?')}
              </Avatar>
            ))}
          </AvatarGroup>
          <Typography variant="body2" color="text.secondary">
            {divers.length} diver{divers.length === 1 ? '' : 's'} call{divers.length === 1 ? 's' : ''} this their home site
          </Typography>
        </Stack>
      )}

      {recentPhotos.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, overflowX: 'auto', pb: 0.5 }}>
          {recentPhotos.map(photo => (
            <Box
              key={photo.key}
              component={RouterLink}
              to={`/post/${photo.postId}`}
              sx={{ flex: '0 0 auto', display: 'block' }}
            >
              <img src={photoUrl(photo.key)} alt="" loading="lazy" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
            </Box>
          ))}
        </Stack>
      )}

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}

      {!loading && posts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>Nobody has posted from here yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Report the conditions — visibility, temperature, current — or post a photo from your dive.
          </Typography>
        </Box>
      )}

      <Stack spacing={0} sx={{ maxWidth: 640 }}>
        {posts.map(post => (
          <PostCard key={post.postId} post={post} currentUserId={userId} onDelete={() => setPosts(prev => prev.filter(p => p.postId !== post.postId))} />
        ))}
      </Stack>

      {site.region && (
        <Button component={RouterLink} to={`/communities/${regionSlug(site.region, site.country ?? '')}`} size="small" sx={{ mt: 1 }}>
          See the {site.region} community
        </Button>
      )}

      {checkInOpen && (
        <CheckInDialog
          open
          site={{ slug: site.slug, name: site.name }}
          onClose={() => setCheckInOpen(false)}
          onCheckedIn={(post) => { if (post.visibility === 'Public') setPosts(prev => [post, ...prev.filter(p => p.postId !== post.postId)]); }}
        />
      )}

      {conditionsOpen && (
        <ConditionsReportDialog
          site={{ slug: site.slug, name: site.name }}
          onClose={() => setConditionsOpen(false)}
          onReported={(post) => {
            setPosts(prev => [post, ...prev.filter(p => p.postId !== post.postId)]);
            postService.getSiteConditions(site.slug).then(setConditions).catch(() => { /* keep the old summary */ });
          }}
        />
      )}

      {composerOpen && (
        <PostComposer
          open
          diveSite={{ slug: site.slug, name: site.name }}
          onClose={() => setComposerOpen(false)}
          onCreated={(post) => {
            showSuccess('Posted to the site community!');
            if (post?.visibility === 'Public') setPosts(prev => [post, ...prev]);
          }}
        />
      )}
    </Paper>
  );
};

export default SiteCommunity;
